import { useEffect, useMemo, useState } from 'react';
import {
  listHistoryOrders,
  listHistoryTasks,
  type HistoryOrderView,
  type PositionGroupHistoryView,
  type TaskView,
} from '@/services/manualTradingApi';
import {
  buildGroupExecutionTree,
  shortTaskId,
  type GroupExecutionNode,
} from '@/lib/groupExecutionTree';

function formatTs(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

function formatUsd(v: number | null | undefined, digits = 2): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toLocaleString('en-US', { maximumFractionDigits: digits, minimumFractionDigits: digits });
}

function formatSpreadPct(ratio: number | null | undefined): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  return `${(ratio * 100).toFixed(4)}%`;
}

function formatSlippage(ratio: number | null | undefined): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  return `${(ratio * 100).toFixed(3)}%`;
}

function taskActionLabel(action: string): string {
  return action === 'Open' ? '开仓' : action === 'Close' ? '关仓' : action;
}

function shortInstructionId(id: string): string {
  if (id.length <= 24) return id;
  return `${id.slice(0, 12)}…`;
}

function TaskInfoTable({ node }: { node: GroupExecutionNode }) {
  const task = node.task;
  const action = task?.action ?? node.orders[0]?.task_action ?? '—';
  const status = task?.status ?? '—';
  const instructionId = node.orders.find((o) => o.instruction_id)?.instruction_id ?? null;

  return (
    <table className="pg-history-task-table">
      <thead>
        <tr>
          <th>动作</th>
          <th>Task ID</th>
          <th>状态</th>
          <th className="num">提交价差</th>
          <th className="num">成交价差</th>
          <th>指令</th>
          <th>更新时间</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{taskActionLabel(action)}</td>
          <td className="mono" title={node.taskId}>
            {shortTaskId(node.taskId)}
          </td>
          <td>{status}</td>
          <td className="num">{formatSpreadPct(task?.commit_spread_ratio)}</td>
          <td className="num">{formatSpreadPct(task?.fill_spread_ratio)}</td>
          <td className="mono" title={instructionId ?? undefined}>
            {instructionId ? shortInstructionId(instructionId) : '—'}
          </td>
          <td>{formatTs(node.updatedAtMs)}</td>
        </tr>
      </tbody>
    </table>
  );
}

function OrderLegSingleTable({ order, label }: { order: HistoryOrderView | undefined; label: string }) {
  return (
    <div className="pg-history-leg-panel">
      <p className="pg-history-leg-label">{label}</p>
      {!order ? (
        <p className="pg-history-leg-empty">暂无 order 明细</p>
      ) : (
        <table className="pg-history-order-table">
          <thead>
            <tr>
              <th>交易所</th>
              <th>合约</th>
              <th>方向</th>
              <th>动作</th>
              <th className="num">名义 USD</th>
              <th className="num">提交价</th>
              <th className="num">成交价</th>
              <th className="num">滑点</th>
              <th>状态</th>
              <th>更新时间</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{order.exchange}</td>
              <td className="mono">{order.inst_id}</td>
              <td>{order.position_side}</td>
              <td>{taskActionLabel(order.action)}</td>
              <td className="num">{formatUsd(order.notional_usd, 0)}</td>
              <td className="num">{formatUsd(order.commit_price, 4)}</td>
              <td className="num">{formatUsd(order.filled_price, 4)}</td>
              <td className="num">{formatSlippage(order.slippage_ratio)}</td>
              <td>{order.status}</td>
              <td>{formatTs(order.updated_at_ms)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

function TaskExecutionRow({ node }: { node: GroupExecutionNode }) {
  const longLeg = node.orders.find((o) => o.position_side === 'Long');
  const shortLeg = node.orders.find((o) => o.position_side === 'Short');

  return (
    <div className="pg-history-execution-row">
      <div className="pg-history-execution-task">
        <TaskInfoTable node={node} />
      </div>
      <div className="pg-history-execution-legs">
        <OrderLegSingleTable order={longLeg} label="多腿 Long" />
        <OrderLegSingleTable order={shortLeg} label="空腿 Short" />
      </div>
    </div>
  );
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; tasks: TaskView[]; orders: HistoryOrderView[] };

export function GroupExecutionDetail({ group }: { group: PositionGroupHistoryView }) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    const params = {
      position_group_id: group.group_id,
      symbol: group.symbol,
      long_exchange: group.long_exchange,
      short_exchange: group.short_exchange,
      limit: 500,
    };

    void Promise.all([listHistoryTasks(params), listHistoryOrders(params)])
      .then(([tasks, orders]) => {
        if (!cancelled) {
          setState({ status: 'ready', tasks, orders });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            status: 'error',
            message: err instanceof Error ? err.message : String(err),
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [group.group_id, group.symbol, group.long_exchange, group.short_exchange]);

  const tree = useMemo(() => {
    if (state.status !== 'ready') return [];
    return buildGroupExecutionTree(state.tasks, state.orders);
  }, [state]);

  if (state.status === 'loading') {
    return <p className="pg-history-detail-hint">加载 task / order 明细…</p>;
  }

  if (state.status === 'error') {
    return <p className="pg-history-detail-error">{state.message}</p>;
  }

  if (tree.length === 0) {
    return (
      <p className="pg-history-detail-empty">
        暂无关联的执行记录（可能尚未归档）。
      </p>
    );
  }

  return (
    <div className="pg-history-detail">
      <p className="pg-history-detail-summary">
        共 {tree.length} 个 task · {state.orders.length} 条 order leg
      </p>
      <div className="pg-history-execution-list">
        {tree.map((node) => (
          <TaskExecutionRow key={node.taskId} node={node} />
        ))}
      </div>
    </div>
  );
}
